import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import { KPIS_QUERY_KEY } from './useKpis';
import { CAJAS_QUERY_KEY } from './useCajas';
import { MOVIMIENTOS_QUERY_KEY } from './useMovimientos';
import { CHART_QUERY_KEY } from './useChartData';
import type { CobroPendiente, PagoPayload } from '../types';

export const COBROS_PENDIENTES_QUERY_KEY = ['contabilidad', 'cobros-pendientes'] as const;

/** Invalida todas las queries de contabilidad que pueden verse afectadas por un cobro. */
function invalidarContabilidad(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: COBROS_PENDIENTES_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: KPIS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: MOVIMIENTOS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: CHART_QUERY_KEY });
}

/** Invalida también las queries del módulo de otros-trámites para el cruce bidireccional. */
function invalidarOtrosTramites(
  queryClient: ReturnType<typeof useQueryClient>,
  otroTramiteId?: string
) {
  queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
  if (otroTramiteId) {
    queryClient.invalidateQueries({ queryKey: ['cobros', otroTramiteId] });
  }
}

export function useCobrosPendientes() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<CobroPendiente[]>({
    queryKey: COBROS_PENDIENTES_QUERY_KEY,
    queryFn: () => contabilidadService.listCobrosPendientes(),
    staleTime: 0,
  });

  const registrarMutation = useMutation({
    mutationFn: ({ cobro, payload }: { cobro: CobroPendiente; payload: PagoPayload }) => {
      if (cobro.origen === 'otro_tramite' && cobro.otroTramiteId) {
        return contabilidadService.registrarCobroOtroTramite(
          cobro.otroTramiteId,
          cobro.moneda,
          payload
        );
      }
      // escritura: cobro contra el total del presupuesto del trámite/moneda
      return contabilidadService.registrarCobro(cobro.tramiteId as number, cobro.moneda, payload);
    },
    onSuccess: (_data, { cobro }) => {
      invalidarContabilidad(queryClient);
      if (cobro.origen === 'otro_tramite') {
        invalidarOtrosTramites(queryClient, cobro.otroTramiteId);
      }
    },
  });

  return {
    cobrosPendientes: data ?? [],
    isLoading,
    error,
    registrarCobro: registrarMutation.mutateAsync,
    isRegistrando: registrarMutation.isPending,
  };
}
